import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { organizationsApi } from '@/api';
import { CopyId, PageHeader } from '@/components/page';
import { EmptyState, MarkTile, Notice } from '@/components/workspace-ui';
import { useCreateOrganization, useToast, useOrganizations, useAuth } from '@/hooks';
import { cn } from '@/lib/utils';

const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const slugify = (s: string) =>
  s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

function validUrl(v: string): boolean {
  if (!v) return true;
  try {
    const u = new URL(v);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

type Errors = { name?: string; slug?: string; website?: string };

function Field({
  id,
  label,
  optional,
  error,
  hint,
  children,
}: {
  id: string;
  label: string;
  optional?: boolean;
  error?: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>
        {label}
        {optional && <span className="ml-1.5 text-xs font-normal text-muted-foreground">optional</span>}
      </Label>
      {children}
      {error ? (
        <p id={`${id}-err`} className="text-xs text-destructive" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-xs text-muted-foreground">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export function OrganizationNewPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { data: organizations = [], isLoading: organizationsLoading } = useOrganizations();
  const createOrg = useCreateOrganization();
  const [step, setStep] = useState<1 | 2>(1);
  const [form, setForm] = useState({ name: '', slug: '', website: '', description: '' });
  const [slugTouched, setSlugTouched] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [checking, setChecking] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const set = (k: keyof typeof form, v: string) => setForm((p) => ({ ...p, [k]: v }));

  // Generate the slug from the name until the user edits it.
  useEffect(() => {
    if (!slugTouched) setForm((p) => ({ ...p, slug: slugify(p.name).slice(0, 48) }));
  }, [form.name, slugTouched]);

  if (organizationsLoading) {
    return (
      <div className="mx-auto max-w-2xl space-y-4" aria-busy="true">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!user?.is_super_admin && organizations.length > 0) {
    return (
      <div className="mx-auto max-w-2xl">
        <PageHeader title="New organization" />
        <div className="rounded-lg border border-border bg-card">
          <EmptyState
            icon={Building2}
            title="One organization per account"
            body={`Your account already belongs to ${organizations[0].name}. An administrator can move your membership from the user management page.`}
            action={
              <Button asChild variant="secondary">
                <Link to="/organizations">Back to organizations</Link>
              </Button>
            }
          />
        </div>
      </div>
    );
  }

  const validateName = (): string | undefined =>
    form.name.trim().length >= 2 ? undefined : 'Enter a name for the organization (at least 2 characters).';

  const validateSlug = async (): Promise<string | undefined> => {
    const v = form.slug.trim();
    if (!v) return 'Enter a slug.';
    if (!SLUG_RE.test(v)) return 'Use lowercase letters, numbers and single hyphens — no spaces.';
    setChecking(true);
    try {
      const r = await organizationsApi.checkSlug(v);
      if (!r.available) return `“${v}” is taken. Try another slug.`;
    } catch {
      /* the server re-checks on create */
    } finally {
      setChecking(false);
    }
    return undefined;
  };

  const validateWebsite = (): string | undefined =>
    validUrl(form.website.trim()) ? undefined : 'Enter a full URL starting with https://';

  const blurSlug = async () => {
    const cleaned = slugify(form.slug).slice(0, 48);
    if (cleaned !== form.slug) set('slug', cleaned);
    if (form.slug) setErrors((p) => ({ ...p, slug: undefined }));
    const err = await validateSlug();
    setErrors((p) => ({ ...p, slug: err }));
  };

  const toReview = async () => {
    const next: Errors = {
      name: validateName(),
      slug: await validateSlug(),
      website: validateWebsite(),
    };
    setErrors(next);
    if (next.name || next.slug || next.website) {
      const id = next.name ? 'f-name' : next.slug ? 'f-slug' : 'f-web';
      document.getElementById(id)?.focus();
      return;
    }
    setStep(2);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 1) {
      await toReview();
      return;
    }
    setSubmitError(null);
    try {
      const org = await createOrg.mutateAsync({
        name: form.name.trim(),
        slug: form.slug.trim(),
        description: form.description.trim() || undefined,
        website: form.website.trim() || undefined,
      });
      toast({
        title: 'Organization created',
        description: `${org.name} is ready. Add members or create its first app.`,
      });
      navigate(`/organizations/${org.id}`);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed to create organization';
      if (/slug/i.test(msg)) {
        setErrors((p) => ({ ...p, slug: msg }));
        setStep(1);
      } else {
        setSubmitError(msg);
      }
    }
  };

  const inputErr = (k: keyof Errors) =>
    cn(errors[k] && 'border-destructive focus-visible:ring-destructive');

  const steps: { n: 1 | 2; label: string }[] = [
    { n: 1, label: 'Details' },
    { n: 2, label: 'Review' },
  ];

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader
        eyebrow={
          <>
            <Link to="/organizations" className="hover:text-foreground hover:underline">
              Organizations
            </Link>
            <span>/</span>
            <span>New</span>
          </>
        }
        title="New organization"
        description="An organization owns apps and OAuth clients. You become its owner and can add members afterwards."
      />

      <nav aria-label="Steps" className="mb-4 flex gap-1.5">
        {steps.map((s) => (
          <button
            key={s.n}
            type="button"
            disabled={s.n > step}
            aria-current={s.n === step ? 'step' : undefined}
            onClick={() => s.n < step && setStep(s.n)}
            className={cn(
              'flex h-8 items-center gap-2 rounded-lg border px-3 text-[12.5px] font-medium disabled:cursor-default',
              s.n === step
                ? 'border-foreground/30 bg-foreground/[0.05] text-foreground'
                : 'border-border text-muted-foreground'
            )}
          >
            <span className="font-mono text-[11px]">{s.n}</span>
            {s.label}
          </button>
        ))}
      </nav>

      <form onSubmit={handleSubmit} noValidate>
        {step === 1 ? (
          <section className="overflow-hidden rounded-lg border border-border bg-card">
            <div className="border-b border-border px-5 py-4">
              <h2 className="text-[14.5px] font-semibold">Details</h2>
              <p className="mt-0.5 text-[12.5px] text-muted-foreground">You can change these later in Settings.</p>
            </div>
            <div className="space-y-4 px-5 py-5">
              <Field id="f-name" label="Name" error={errors.name}>
                <Input
                  id="f-name"
                  value={form.name}
                  required
                  maxLength={80}
                  autoFocus
                  autoComplete="organization"
                  placeholder="Northwind Labs"
                  aria-invalid={!!errors.name}
                  aria-describedby={errors.name ? 'f-name-err' : undefined}
                  className={inputErr('name')}
                  onChange={(e) => set('name', e.target.value)}
                  onBlur={() => setErrors((p) => ({ ...p, name: form.name ? validateName() : undefined }))}
                />
              </Field>
              <Field
                id="f-slug"
                label="Slug"
                error={errors.slug}
                hint={checking ? 'Checking availability…' : 'Generated from the name. Lowercase letters, numbers and hyphens.'}
              >
                <Input
                  id="f-slug"
                  value={form.slug}
                  required
                  maxLength={48}
                  spellCheck={false}
                  autoCapitalize="off"
                  placeholder="northwind"
                  aria-invalid={!!errors.slug}
                  aria-describedby={errors.slug ? 'f-slug-err' : 'f-slug-hint'}
                  className={cn('font-mono', inputErr('slug'))}
                  onChange={(e) => {
                    setSlugTouched(e.target.value.length > 0);
                    set('slug', e.target.value);
                  }}
                  onBlur={blurSlug}
                />
              </Field>
              <Field id="f-web" label="Website" optional error={errors.website}>
                <Input
                  id="f-web"
                  type="url"
                  inputMode="url"
                  value={form.website}
                  placeholder="https://example.com"
                  aria-invalid={!!errors.website}
                  aria-describedby={errors.website ? 'f-web-err' : undefined}
                  className={inputErr('website')}
                  onChange={(e) => set('website', e.target.value)}
                  onBlur={() => setErrors((p) => ({ ...p, website: validateWebsite() }))}
                />
              </Field>
              <Field
                id="f-desc"
                label="Description"
                optional
                hint={
                  <span className="font-mono">
                    {form.description.length} / 280
                  </span>
                }
              >
                <Textarea
                  id="f-desc"
                  value={form.description}
                  maxLength={280}
                  placeholder="What this organization does"
                  onChange={(e) => set('description', e.target.value)}
                />
              </Field>
            </div>
            <div className="flex flex-col-reverse gap-2 border-t border-border px-5 py-3 sm:flex-row sm:justify-end">
              <Button asChild variant="secondary">
                <Link to="/organizations">Cancel</Link>
              </Button>
              <Button type="submit" disabled={checking}>
                Continue
                <ArrowRight />
              </Button>
            </div>
          </section>
        ) : (
          <section className="overflow-hidden rounded-lg border border-border bg-card">
            <div className="border-b border-border px-5 py-4">
              <h2 className="text-[14.5px] font-semibold">Review</h2>
              <p className="mt-0.5 text-[12.5px] text-muted-foreground">
                Check the details before creating the organization.
              </p>
            </div>
            <div className="space-y-5 px-5 py-5">
              <div className="flex items-center gap-3">
                <MarkTile name={form.name} size="lg" />
                <div className="min-w-0">
                  <p className="truncate text-[15px] font-semibold">{form.name.trim()}</p>
                  <CopyId value={form.slug} />
                </div>
              </div>
              <dl className="grid grid-cols-[110px_1fr] gap-x-4 gap-y-3 text-[13.5px]">
                <dt className="text-muted-foreground">Name</dt>
                <dd>{form.name.trim()}</dd>
                <dt className="text-muted-foreground">Slug</dt>
                <dd className="font-mono">{form.slug}</dd>
                <dt className="text-muted-foreground">Website</dt>
                <dd className="break-all">
                  {form.website.trim() || <span className="text-muted-foreground">Not set</span>}
                </dd>
                <dt className="text-muted-foreground">Description</dt>
                <dd>{form.description.trim() || <span className="text-muted-foreground">None</span>}</dd>
                <dt className="text-muted-foreground">Owner</dt>
                <dd>{user?.name || user?.email}</dd>
              </dl>
              {user?.is_super_admin ? (
                <Notice tone="info" title="You’ll manage this as super admin">
                  Super admins create organizations without joining them. Add an owner from the organization’s Members tab.
                </Notice>
              ) : (
                <Notice tone="info" title="You’ll be the owner">
                  {user?.name} ({user?.email}) is added as owner. Add admins and members from the organization’s Members
                  tab.
                </Notice>
              )}
              {submitError && (
                <Notice tone="bad" title="Couldn’t create the organization">
                  {submitError}
                </Notice>
              )}
            </div>
            <div className="flex flex-col-reverse gap-2 border-t border-border px-5 py-3 sm:flex-row sm:justify-between">
              <Button type="button" variant="secondary" onClick={() => setStep(1)}>
                <ArrowLeft />
                Back
              </Button>
              <Button type="submit" disabled={createOrg.isPending}>
                {createOrg.isPending ? 'Creating…' : 'Create organization'}
              </Button>
            </div>
          </section>
        )}
      </form>
    </div>
  );
}
